import { DataItem, DivergenceItem } from "@/types";

export const getDisplayValue = (item: DataItem, keys: string[]): string => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== null && value !== undefined && value !== "") {
      const text = String(value);
      if (key === "trend_reason") return text;
      return text.substring(0, 50);
    }
  }
  return "-";
};

export const formatNumber = (value?: number, decimals = 2): string | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value.toFixed(decimals);
};

export const formatSigned = (value?: number, decimals = 2): string | null => {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return `${value >= 0 ? "+" : ""}${value.toFixed(decimals)}`;
};

export const formatDate = (value?: string): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(parsed);
};

export const formatPatternName = (name: string | undefined | null): string => {
  if (!name) return "-";
  const normalized = String(name).toUpperCase();
  if (normalized === "UNKNOWN") return "Flat / No Volume";
  return normalized.replace(/_/g, " ");
};

export const getDivergenceSummary = (item: DivergenceItem): string => {
  const type = item.type || "";
  const isHidden = type.includes("HIDDEN");
  const isBullish = type.includes("BULLISH");
  const isBearish = type.includes("BEARISH");

  const divergenceLabel = isHidden
    ? (isBullish ? "Hidden Bullish" : isBearish ? "Hidden Bearish" : "Hidden Divergence")
    : (isBullish ? "Bullish" : isBearish ? "Bearish" : "Divergence");

  const priceMoveLabel = isHidden
    ? (isBullish ? "price made a higher low" : isBearish ? "price made a lower high" : "price diverged")
    : (isBullish ? "price made a lower low" : isBearish ? "price made a higher high" : "price diverged");

  const implication = isHidden
    ? (isBullish
        ? "This can signal trend continuation in an uptrend."
        : isBearish
          ? "This can signal trend continuation in a downtrend."
          : "This can signal a potential change in momentum.")
    : (isBullish
        ? "This can signal weakening selling pressure and a potential rebound."
        : isBearish
          ? "This can signal weakening buying momentum and a potential pullback."
          : "This can signal a potential change in momentum.");

  const price1 = typeof item.price_pivot_1 === "number" ? item.price_pivot_1 : null;
  const price2 = typeof item.price_pivot_2 === "number" ? item.price_pivot_2 : null;
  const rsi1 = typeof item.rsi_pivot_1 === "number" ? item.rsi_pivot_1 : null;
  const rsi2 = typeof item.rsi_pivot_2 === "number" ? item.rsi_pivot_2 : null;

  const date1 = formatDate(item.pivot_1_date);
  const date2 = formatDate(item.pivot_2_date);
  const dateRange = date1 && date2 ? ` between ${date1} and ${date2}` : "";

  let priceMove = priceMoveLabel + dateRange;
  if (price1 !== null && price2 !== null) {
    const priceDelta = price2 - price1;
    const pricePct = price1 !== 0 ? (priceDelta / price1) * 100 : null;
    const price1Text = formatNumber(price1);
    const price2Text = formatNumber(price2);
    if (price1Text && price2Text) {
      const priceText = `from ${price1Text} to ${price2Text}`;
      const pricePctText = pricePct !== null && Number.isFinite(pricePct) ? ` (${formatSigned(pricePct)}%)` : "";
      priceMove = `${priceMoveLabel}${dateRange}, moving ${priceText}${pricePctText}`;
    }
  }

  let rsiMove = "RSI moved in the opposite direction";
  if (rsi1 !== null && rsi2 !== null) {
    const rsiDelta = typeof item.rsi_diff === "number" ? item.rsi_diff : rsi2 - rsi1;
    const rsi1Text = formatNumber(rsi1);
    const rsi2Text = formatNumber(rsi2);
    if (rsi1Text && rsi2Text) {
      const rsiText = `from ${rsi1Text} to ${rsi2Text}`;
      const rsiDeltaText = formatSigned(rsiDelta);
      rsiMove = `RSI moved ${rsiText}${rsiDeltaText ? ` (${rsiDeltaText})` : ""}`;
    }
  }

  return `${divergenceLabel} divergence: ${priceMove}, while ${rsiMove}. ${implication}`;
};

export const formatWeekRange = (scanDate?: string): string | null => {
  if (!scanDate) return null;
  const end = new Date(scanDate);
  if (Number.isNaN(end.getTime())) return null;
  // Use last completed Mon–Fri week before or on scanDate.
  const day = end.getDay(); // Sun=0, Mon=1, ... Fri=5
  const daysToFriday = day <= 5 ? 5 - day : 6; // if Sat, roll to next Fri (then we'll subtract 7)
  const friday = new Date(end);
  friday.setDate(end.getDate() + daysToFriday);
  if (friday > end) {
    friday.setDate(friday.getDate() - 7);
  }
  const start = new Date(friday);
  start.setDate(friday.getDate() - 4);
  
  // Format as DD-MM
  const startDD = String(start.getDate()).padStart(2, "0");
  const startMM = String(start.getMonth() + 1).padStart(2, "0");
  const endDD = String(friday.getDate()).padStart(2, "0");
  const endMM = String(friday.getMonth() + 1).padStart(2, "0");
  
  return `${startDD}-${startMM} to ${endDD}-${endMM}`;
};

export const formatMonthRange = (scanDate?: string): string | null => {
  if (!scanDate) return null;
  const base = new Date(scanDate);
  if (Number.isNaN(base.getTime())) return null;
  // Use last completed month relative to scanDate.
  const year = base.getFullYear();
  const month = base.getMonth(); // 0-based
  const prevMonthEnd = new Date(year, month, 0); // day 0 = last day of previous month
  
  const monthName = new Intl.DateTimeFormat("en-US", { month: "long" }).format(prevMonthEnd);
  const monthYear = `${monthName} ${prevMonthEnd.getFullYear()}`;
  
  return monthYear;
};
